"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Loader, Leaf, ArrowRight, CircleCheck } from "lucide-react";
function InquiryFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="147716" className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
                <div className="size-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                    <CircleCheck className="size-8 text-primary" />
                </div>
                <h3 className="text-2xl font-serif font-bold text-foreground">Request Received</h3>
                <p className="text-muted-foreground max-w-sm">{successMessage}</p>
                <Button onClick={resetForm} variant="outline" className="mt-6 rounded-full px-8">
                    Submit Another Request
                </Button>
            </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Name must be at least 2 characters"
        }
      }} render={({
        field
      }) => <FormItem className="col-span-1 md:col-span-2">
                            <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Your Name</FormLabel>
                            <FormControl>
                                <Input placeholder="John Doe" className="bg-background/50 border-border/50 focus-visible:ring-primary focus-visible:border-primary h-12 rounded-xl transition-all duration-300" {...field} />
                            </FormControl>
                            <FormDescription className="text-[10px]">Enter your full name.</FormDescription>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Email Address</FormLabel>
                            <FormControl>
                                <Input type="email" placeholder="john@example.com" className="bg-background/50 border-border/50 focus-visible:ring-primary focus-visible:border-primary h-12 rounded-xl transition-all duration-300" {...field} />
                            </FormControl>
                            <FormDescription className="text-[10px]">We'll use this to contact you.</FormDescription>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Phone Number</FormLabel>
                            <FormControl>
                                <Input type="tel" placeholder="(303) 555-1234" className="bg-background/50 border-border/50 focus-visible:ring-primary focus-visible:border-primary h-12 rounded-xl transition-all duration-300" {...field} />
                            </FormControl>
                            <FormDescription className="text-[10px]">Required – for quicker coordination.</FormDescription>
                            <FormMessage />
                        </FormItem>} />
            </div>

            <FormField control={control} name="message" rules={{
      minLength: {
        value: 10,
        message: "Please provide a bit more detail (min 10 characters)"
      }
    }} render={({
      field
    }) => <FormItem>
                        <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Primary Concerns</FormLabel>
                        <FormControl>
                            <Textarea placeholder="Describe your current situation or specific questions..." className="bg-background/50 border-border/50 focus-visible:ring-primary focus-visible:border-primary min-h-[120px] rounded-xl resize-none transition-all duration-300" {...field} />
                        </FormControl>
                        <FormDescription className="text-[10px]">Minimum 10 characters if provided – briefly describe what you would like to discuss.</FormDescription>
                        <FormMessage />
                    </FormItem>} />

            <FormField control={control} name="preferred_date" render={({
      field
    }) => <FormItem>
                        <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Preferred Date for Call (optional)</FormLabel>
                        <FormControl>
                            <Input type="date" className="bg-background/50 border-border/50 focus-visible:ring-primary focus-visible:border-primary h-12 rounded-xl transition-all duration-300" {...field} />
                        </FormControl>
                        <FormDescription className="text-[10px]">If you have a specific day in mind, let us know.</FormDescription>
                        <FormMessage />
                    </FormItem>} />

            {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-xl text-destructive text-sm flex items-start gap-3">
                    <span className="mt-0.5"></span>
                    <p>{submitError}</p>
                </div>}

            <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-lg font-bold rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/20 hover:shadow-primary/30 transition-all duration-300 group">
                {isSubmitting ? <>
                        <Loader className="animate-spin mr-2 size-5" />
                        Scheduling...
                    </> : <>
                        Request Free Consultation
                        <ArrowRight className="ml-2 size-5 group-hover:translate-x-1 transition-transform" />
                    </>}
            </Button>
        </Form>;
}
export default function Inquiry() {
  const {
    ref: sectionRef
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: formRef,
    isVisible: isFormVisible
  } = useRevealOnScroll({
    threshold: 0.2,
    once: true
  });
  return <section id="freeconsultation" className="relative py-24 lg:py-32 bg-background text-foreground overflow-hidden" ref={sectionRef}>
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                <div className="absolute -top-[20%] -right-[10%] w-[70%] h-[70%] rounded-full bg-primary/5 blur-3xl opacity-50" />
                <div className="absolute -bottom-[20%] -left-[10%] w-[60%] h-[60%] rounded-full bg-accent/5 blur-3xl opacity-50" />
            </div>

            <div className="container mx-auto px-4 md:px-6 relative z-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
                    
                    <div className="flex flex-col transition-all duration-1000 ease-out opacity-100 translate-x-0 !justify-start lg:col-span-5">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-chart-2/10 text-chart-2 text-sm font-medium w-fit mb-6">
                            <Leaf className="size-4" />
                            <span>Expert Navigation Support</span>
                        </div>
                        
                        <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-foreground leading-tight tracking-wide mb-6">
                            Navigate Your <br className="hidden lg:block" />
                            <span className="text-primary">Cancer Journey</span> <br className="hidden lg:block" />
                            with Confidence.
                        </h2>
                        
                        <p className="text-lg text-muted-foreground mb-8 max-w-md leading-relaxed">
                            Schedule a complimentary 15-minute consultation with The Prostate Cancer Navigator. I help you interpret results, explore treatment paths, and prepare for critical medical visits.
                        </p>

                        <div className="flex items-center gap-6 text-sm font-medium text-foreground">
                            <div className="flex items-center gap-2">
                                <div className="size-10 rounded-full bg-accent/50 flex items-center justify-center text-accent-foreground">
                                    <CircleCheck className="size-5" />
                                </div>
                                <span>Personalized Care</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="size-10 rounded-full bg-accent/50 flex items-center justify-center text-accent-foreground">
                                    <CircleCheck className="size-5" />
                                </div>
                                <span>Professional Guidance</span>
                            </div>
                        </div>
                    </div>

                    <div ref={formRef} className={`lg:col-span-7 lg:pl-12 flex flex-col h-full transition-all duration-1000 delay-200 ease-out ${isFormVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
                        <Card className="h-full bg-card/80 backdrop-blur-xl border-border/40 shadow-2xl shadow-primary/5 rounded-3xl overflow-hidden relative">
                            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-accent-foreground to-primary opacity-70" />
                            
                            <CardHeader className="pb-6 pt-8 px-8">
                                <CardTitle className="text-2xl font-serif font-bold">Consultation Request</CardTitle>
                                <CardDescription className="text-base">
                                    Share a few details below and I will reach out to schedule our initial 15-minute conversation.
                                </CardDescription>
                            </CardHeader>
                            
                            <CardContent className="px-8 pb-8">
                                <FormProvider formKey="free_consultation_form_services" formId="free_consultation_form_services" sectionName="FreeConsultation" defaultValues={{
                name: "",
                email: "",
                phone: "",
                preferred_date: "",
                message: ""
              }} validationRules={{
                name: {
                  required: true,
                  minLength: 2
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                phone: {
                  required: true,
                  pattern: "phone"
                }
              }} submitText="Request Free Consultation" successMessage="Thank you. I've received your request and will contact you shortly to schedule our 15-minute consultation.">
                                    <InquiryFormInner />
                                </FormProvider>
                            </CardContent>
                        </Card>
                    </div>

                </div>
            </div>
        </section>;
}